/**
 * @voltxampere/presets — Op-amp voltage-follower buffer (#12)
 *
 * Unity-gain buffer: out tied directly back to in_neg; in_pos
 * driven by V1. The closed-loop gain is exactly 1 — V(out) = V_in
 * regardless of source impedance.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { makeCircuit, makeNet, makeComponent } from "../_builders.js";
export const opampBuffer = {
    id: "opamp-buffer",
    name: { tr: "Op-Amp Tampon (Voltaj İzleyici)", en: "Op-Amp Buffer (Voltage Follower)" },
    category: "amplifier",
    difficulty: 3,
    description: {
        tr: "Birim kazançlı tampon: V(out) = V(in). Yüksek giriş, düşük çıkış empedansı.",
        en: "Unity-gain buffer: V(out) = V(in). High input, low output impedance.",
    },
    // Sprint 25 R2 lane 4D-A — hand-authored layout. V1 left of the
    // op-amp on a 20-px grid, GND1 below V1, op-amp body centered at
    // origin. Unity-gain feedback wraps from `out` over the top of
    // the op-amp into the in_neg pin; in_pos approached from below
    // via x=0 to avoid the in_neg/out feedback rail at x=20.
    circuit: makeCircuit({
        slot: 12,
        name: "Op-Amp Buffer",
        description: "Voltage follower (unity-gain)",
        nets: [
            makeNet({ name: "gnd", isGround: true }),
            makeNet({ name: "in" }),
            makeNet({ name: "out" }),
        ],
        components: [
            makeComponent({
                id: "V1",
                type: "voltage-source",
                params: { voltage: 1 },
                terminals: { pos: "net_in", neg: "net_gnd" },
                label: "V1",
                position: { x: -200, y: 0 },
                rotation: 0,
            }),
            makeComponent({
                id: "U1",
                type: "opamp",
                params: {},
                terminals: { in_pos: "net_in", in_neg: "net_out", out: "net_out" },
                label: "U1",
                position: { x: 40, y: 0 },
                rotation: 0,
            }),
            makeComponent({
                id: "GND1",
                type: "ground",
                params: {},
                terminals: { p: "net_gnd" },
                position: { x: -200, y: 80 },
                rotation: 0,
            }),
        ],
        wires: [
            // net_in: V1.pos (-200,-10) → in_pos (20,4). Goes up and over
            // the top of the op-amp via x=0 to avoid colliding with the
            // unity-gain feedback rail at x=20.
            {
                id: "w_in",
                netId: "net_in",
                path: [
                    { x: -200, y: -10 },
                    { x: -200, y: -40 },
                    { x: 0, y: -40 },
                    { x: 0, y: 4 },
                    { x: 20, y: 4 },
                ],
                style: "orthogonal",
            },
            // net_out: unity-gain feedback. U1.out (60,0) wraps around
            // the top to in_neg (20,-4) via y=-60.
            {
                id: "w_out",
                netId: "net_out",
                path: [
                    { x: 60, y: 0 },
                    { x: 60, y: -60 },
                    { x: 20, y: -60 },
                    { x: 20, y: -4 },
                ],
                style: "orthogonal",
            },
            // net_gnd: V1.neg (-200,10) → GND1.p (-200,71).
            {
                id: "w_gnd",
                netId: "net_gnd",
                path: [
                    { x: -200, y: 10 },
                    { x: -200, y: 71 },
                ],
                style: "orthogonal",
            },
        ],
    }),
    expectedValues: [
        {
            node: "out",
            analysis: "dc",
            value: 1,
            tolerance: 0.01,
            unit: "V",
        },
    ],
    learningGoals: [
        {
            tr: "Birim kazanç: V(out)=V(in). Empedans uydurucusu olarak kullanılır.",
            en: "Unity gain: V(out)=V(in). Used as an impedance matcher.",
        },
        {
            tr: "Yüksek giriş empedansı (~ ∞), düşük çıkış empedansı (~ 0).",
            en: "High input impedance (~ ∞), low output impedance (~ 0).",
        },
    ],
    references: ["Horowitz/Hill, The Art of Electronics, §4.2"],
};
//# sourceMappingURL=opamp-buffer.js.map